import { useState } from 'react';

type AlertBannerVariant = 'warning' | 'info' | 'error' | 'success';

const VARIANT_CLASSES: Record<AlertBannerVariant, {
  wrapper: string;
  icon: string;
  btn: string;
}> = {
  warning: {
    wrapper: 'bg-warning-bg border-b border-[color:var(--warning-border)]',
    icon: 'text-warning',
    btn: 'bg-[var(--warning-hover)] hover:bg-[var(--warning)] text-white',
  },
  info: {
    wrapper: 'bg-info-bg border-b border-[color:var(--info-border)]',
    icon: 'text-info',
    btn: 'bg-[var(--info-hover)] hover:bg-[var(--info)] text-white',
  },
  error: {
    wrapper: 'bg-error-bg border-b border-[color:var(--error-border)]',
    icon: 'text-error',
    btn: 'bg-[var(--error-hover)] hover:bg-[var(--error)] text-white',
  },
  success: {
    wrapper: 'bg-success-bg border-b border-[color:var(--success-border)]',
    icon: 'text-success',
    btn: 'bg-[var(--success-hover)] hover:bg-[var(--success)] text-white',
  },
};

const WarningIcon = ({ className }: { className?: string }) => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" className={className} style={{ flexShrink: 0 }}>
    <path d="M8 2.5L1.5 13.5h13L8 2.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    <path d="M8 7v2.5M8 11.2h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const InfoIcon = ({ className }: { className?: string }) => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" className={className} style={{ flexShrink: 0 }}>
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M8 7.3v3.4M8 5.1h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const ErrorIcon = ({ className }: { className?: string }) => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" className={className} style={{ flexShrink: 0 }}>
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M8 5.3v3.4M8 11.2h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const SuccessIcon = ({ className }: { className?: string }) => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" className={className} style={{ flexShrink: 0 }}>
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M5.3 8.2l1.9 1.9 3.5-3.9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const VARIANT_ICONS: Record<AlertBannerVariant, (props: { className?: string }) => React.ReactElement> = {
  warning: WarningIcon,
  info: InfoIcon,
  error: ErrorIcon,
  success: SuccessIcon,
};

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M3 3l8 8M11 3L3 11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

export interface AlertBannerProps {
  variant?: AlertBannerVariant;
  children: React.ReactNode;
  ctaLabel?: string;
  onCta?: () => void;
  dismissible?: boolean;
}

export const AlertBanner = ({
  variant = 'warning',
  children,
  ctaLabel,
  onCta,
  dismissible = true,
}: AlertBannerProps) => {
  const [visible, setVisible] = useState(true);
  const c = VARIANT_CLASSES[variant];
  const Icon = VARIANT_ICONS[variant];

  if (!visible) return null;

  return (
    <div className={`flex items-center gap-3 shrink-0 px-5 py-[9px] ${c.wrapper}`}>
      <Icon className={c.icon} />

      <span className="flex-1 min-w-0 truncate text-secondary" style={{ fontSize: '12.5px', lineHeight: 1.4 }}>
        {children}
      </span>

      {ctaLabel && (
        <button type="button"
          onClick={onCta}
          className={`inline-flex items-center shrink-0 rounded-[6px] px-[12px] py-[4px] text-[12.5px] font-medium whitespace-nowrap cursor-pointer border-none ${c.btn}`}
        >
          {ctaLabel}
        </button>
      )}

      {dismissible && (
        <button type="button"
          onClick={() => setVisible(false)}
          aria-label="Ocultar banner"
          className={`flex items-center shrink-0 p-1 bg-transparent border-none cursor-pointer opacity-50 ${c.icon}`}
        >
          <CloseIcon />
        </button>
      )}
    </div>
  );
};



